import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListSubheader,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import dashboardApi from '../../services/dashboardApi';
import votingService from '../../services/votingService';
import {
  DashboardAreaOption,
  DashboardCard,
  DashboardOverview,
  DashboardTrends,
  DemoVoteAutomationStatus,
  MapFeatureProperties,
  TimeRangePreset,
} from '../../types/dashboard';
import { buildTimeRange, formatRangeLabel } from '../../utils/dashboardTimeRange';
import {
  getCityLogoByPercentage,
  getColorForPercentage,
  getEmotionIconByPercentage,
  getNoVotesEmotionIcon,
  NO_VOTES_COLOR,
} from '../../utils/emotionUtils';
import dashboardAccessLogo from '../../assets/logos/hwa-nyc-95-100.svg';
import instagramIcon from '../../assets/icons/instagram.svg';
import tiktokIcon from '../../assets/icons/tik-tok.svg';
import blueskyIcon from '../../assets/icons/bluesky.svg';
import DashboardMap from './DashboardMap';
import DashboardQotdTab from './DashboardQotdTab';
import './DashboardPage.css';

const DASHBOARD_ACCESS_KEY = 'hwa_dashboard_access_granted';
const FAVORITE_KEY = 'hwa_dashboard_favorite_neighborhood';
const ALERT_SENTIMENT_DROP_THRESHOLD = -10;
const ALERT_VOTE_SPIKE_THRESHOLD = 250;
const DASHBOARD_AUTO_REFRESH_MS = 30 * 1000;

const isAutoRefreshPreset = (preset: TimeRangePreset) => preset !== 'custom' && preset !== 'yesterday';

const timeRangeOptions: { value: TimeRangePreset; label: string }[] = [
  { value: 'live', label: 'Live (last 60 minutes)' },
  { value: 'last_24_hours', label: 'Last 24 Hours' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week (Sun-Sat)' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_365_days', label: 'Last 365 Days' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom', label: 'Custom Range' },
];

const levelOptions = [
  { value: 'city', label: 'City' },
  { value: 'borough', label: 'Borough' },
  { value: 'neighborhood', label: 'Neighborhood' },
  { value: 'block_group', label: 'Block Group' },
] as const;

const trendMetricOptions = [
  { value: 'sentiment', label: 'Sentiment Index' },
  { value: 'votes', label: 'Votes' },
] as const;

type InsightSample = {
  id: string;
  title: string;
  excerpt: string;
  tag?: string;
  tagIcon?: 'traffic' | 'attach_money';
};

const insightSamples: InsightSample[] = [
  {
    id: 'insight-1',
    title: 'Brooklyn outperforms weekdays but softens on weekends',
    excerpt:
      'Brooklyn shows stronger sentiment than Manhattan from Monday to Thursday, with more consistent positive voting during daytime hours. On weekends, that lead narrows and turns slightly negative in several high-activity zones.',
  },
  {
    id: 'insight-2',
    title: 'Morning traffic pressure aligns with lower sentiment',
    excerpt:
      'Between 7:00 and 9:30 AM, areas with heavier commute corridors show a repeated drop in sentiment compared with midday. The effect is strongest on weekdays and fades after peak inbound traffic windows.',
    tag: 'Linked to public traffic dataset',
    tagIcon: 'traffic',
  },
  {
    id: 'insight-3',
    title: "Sentiment pressure is higher in NYC Opportunity poverty clusters",
    excerpt:
      "Areas with higher NYC Opportunity poverty rates show lower baseline sentiment and slower rebound after negative spikes. Adjacent neighborhoods with stronger income stability recover faster under similar vote volumes.",
    tag: 'Linked to public poverty dataset',
    tagIcon: 'attach_money',
  },
];

const formatVotes = (value: number) => new Intl.NumberFormat('en-US').format(value);

const formatSentiment = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}`;
const formatMoverDelta = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}`;

const formatConfidenceBadge = (confidence: number) =>
  confidence >= 0.95 ? 'High confidence' : 'Limited data';

const dedupeAreaOptions = (options: DashboardAreaOption[]) => {
  const byId = new Map<string, DashboardAreaOption>();
  options.forEach((option) => {
    if (!byId.has(option.id)) {
      byId.set(option.id, option);
    }
  });
  return Array.from(byId.values());
};

const getPositivePercentage = (card: DashboardCard) => {
  if (!card.total_votes) return 50;
  return (card.good_votes / card.total_votes) * 100;
};

const MiniSparkline = ({ points }: { points: { bucket: string; value: number }[] }) => {
  if (!points.length) return null;
  const chartValues = points.map((point) => [new Date(point.bucket).getTime(), point.value]);
  const maxValue = Math.max(...points.map((point) => point.value), 0);
  const lowCutoff = maxValue * 0.34;
  const highCutoff = maxValue * 0.67;

  const option: EChartsOption = {
    animation: false,
    grid: { left: 0, right: 0, top: 2, bottom: 0 },
    xAxis: { type: 'time', show: false },
    yAxis: { type: 'value', show: false },
    visualMap: [
      {
        type: 'piecewise',
        show: false,
        seriesIndex: 0,
        dimension: 1,
        pieces: maxValue <= 0
          ? [{ gte: 0, color: getColorForPercentage(55) }]
          : [
              { gte: highCutoff, color: getColorForPercentage(90) },
              { gte: lowCutoff, lt: highCutoff, color: getColorForPercentage(50) },
              { lt: lowCutoff, color: getColorForPercentage(10) },
            ],
      },
    ],
    series: [
      {
        type: 'line',
        data: chartValues,
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 2.2,
        },
        areaStyle: {
          color: 'rgba(159, 190, 24, 0.14)',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1f232a',
      borderColor: 'rgba(255,255,255,0.14)',
      textStyle: { color: '#f1f4f8', fontSize: 12 },
      formatter: (params: any) => {
        const row = Array.isArray(params) ? params[0] : params;
        const value = Number(Array.isArray(row?.value) ? row.value[1] : 0);
        const ts = Number(Array.isArray(row?.value) ? row.value[0] : 0);
        const timeLabel = new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(ts));
        return `<div>${timeLabel}</div><div style="margin-top:4px"><strong>${formatVotes(value)}</strong> votes</div>`;
      },
    },
  };
  return (
    <Box className="dashboard-engagement-sparkline">
      <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />
    </Box>
  );
};

type TrendMetric = 'sentiment' | 'votes';

type TimeLabelMode = 'hour' | 'weekday' | 'day' | 'month';

const resolveTimeLabelMode = (preset: TimeRangePreset, startAt: string, endAt: string): TimeLabelMode => {
  if (preset === 'live' || preset === 'last_24_hours' || preset === 'today' || preset === 'yesterday') return 'hour';
  if (preset === 'this_week' || preset === 'last_7_days') return 'weekday';
  if (preset === 'this_year' || preset === 'last_365_days' || preset === 'all_time') return 'month';

  const spanMs = new Date(endAt).getTime() - new Date(startAt).getTime();
  const spanDays = spanMs / (1000 * 60 * 60 * 24);
  if (spanDays <= 2) return 'hour';
  if (spanDays <= 10) return 'weekday';
  if (spanDays <= 90) return 'day';
  return 'month';
};

const formatAxisTime = (value: number, mode: TimeLabelMode) => {
  const date = new Date(value);
  switch (mode) {
    case 'hour':
      return new Intl.DateTimeFormat('en-US', { hour: '2-digit' }).format(date);
    case 'weekday':
      return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
    case 'day':
      return new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(date);
    case 'month':
    default:
      return new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
  }
};

const formatTooltipTime = (value: number) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const formatDemoRun = (value: string | null) => {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const TrendChart = ({
  trends,
  compareOn,
  metric,
  preset,
  startAt,
  endAt,
}: {
  trends: DashboardTrends | null;
  compareOn: boolean;
  metric: TrendMetric;
  preset: TimeRangePreset;
  startAt: string;
  endAt: string;
}) => {
  if (!trends || trends.current.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No trend data for this selection.
        </Typography>
      </Box>
    );
  }

  const valueKey = metric === 'sentiment' ? 'sentiment_index' : 'votes';
  const durationMs = new Date(endAt).getTime() - new Date(startAt).getTime();
  const labelMode = resolveTimeLabelMode(preset, startAt, endAt);

  const currentSeries = trends.current.map((point) => [
    new Date(point.bucket).getTime(),
    point[valueKey],
  ]);
  const previousSeries = compareOn
    ? trends.previous.map((point) => [new Date(point.bucket).getTime() + durationMs, point[valueKey]])
    : [];
  const allValues = [...currentSeries, ...previousSeries].map((item) => Number(item[1]));
  const minValue = Math.min(...allValues, metric === 'sentiment' ? -20 : 0);
  const maxValue = Math.max(...allValues, metric === 'sentiment' ? 20 : 1);

  const dynamicLineColorPieces = [
    { gte: 40, color: getColorForPercentage(90) },
    { gte: 20, lt: 40, color: getColorForPercentage(75) },
    { gte: 0, lt: 20, color: getColorForPercentage(55) },
    { gte: -20, lt: 0, color: getColorForPercentage(35) },
    { lt: -20, color: getColorForPercentage(10) },
  ];

  const series: any[] = [
    {
      name: 'Current period',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: currentSeries,
      lineStyle: {
        width: 3,
        color: metric === 'sentiment' ? undefined : '#32A936',
      },
      areaStyle: {
        color: 'rgba(50, 169, 54, 0.16)',
      },
    },
  ];

  if (compareOn) {
    series.push({
      name: 'Previous period',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: previousSeries,
      lineStyle: {
        width: 2,
        type: 'dashed',
        color: '#6FA6E8',
      },
      areaStyle: {
        color: 'rgba(111, 166, 232, 0.08)',
      },
    });
  }

  const option: EChartsOption = {
    animation: true,
    grid: { left: 18, right: 18, top: 24, bottom: 42, containLabel: true },
    legend: {
      top: 0,
      right: 0,
      textStyle: { color: '#c7cfdb', fontSize: 12 },
      data: compareOn ? ['Current period', 'Previous period'] : ['Current period'],
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1f232a',
      borderColor: 'rgba(255,255,255,0.14)',
      textStyle: { color: '#f1f4f8' },
      formatter: (params: any) => {
        const rows = Array.isArray(params) ? params : [params];
        const axisValue = Number(rows[0]?.axisValue || rows[0]?.value?.[0] || 0);
        const title = formatTooltipTime(axisValue);
        const values = rows
          .map((row: any) => {
            const rawValue = Number(Array.isArray(row.value) ? row.value[1] : row.value);
            const formattedValue =
              metric === 'sentiment' ? `${rawValue.toFixed(1)} SI` : `${formatVotes(Math.round(rawValue))} votes`;
            return `${row.marker}${row.seriesName}: <strong>${formattedValue}</strong>`;
          })
          .join('<br/>');
        return `<div>${title}</div><div style="margin-top:6px">${values}</div>`;
      },
    },
    xAxis: {
      type: 'time',
      min: new Date(startAt).getTime(),
      max: new Date(endAt).getTime(),
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.22)' } },
      axisLabel: {
        color: '#b6c0cf',
        formatter: (value: number) => formatAxisTime(value, labelMode),
      },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
    },
    yAxis: {
      type: 'value',
      min: metric === 'sentiment' ? Math.floor(minValue / 10) * 10 : 0,
      max: metric === 'sentiment' ? Math.ceil(maxValue / 10) * 10 : undefined,
      axisLine: { show: false },
      axisLabel: {
        color: '#b6c0cf',
        formatter: (value: number) => (metric === 'sentiment' ? `${value}` : formatVotes(Number(value))),
      },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.08)' } },
    },
    visualMap:
      metric === 'sentiment'
        ? [
            {
              type: 'piecewise',
              show: false,
              seriesIndex: 0,
              dimension: 1,
              pieces: dynamicLineColorPieces,
            },
          ]
        : [],
    series,
  };

  return (
    <Box sx={{ width: '100%', height: 300, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, p: 1 }}>
      <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />
    </Box>
  );
};

const DashboardPage = () => {
  const [password, setPassword] = useState('');
  const [accessGranted, setAccessGranted] = useState(localStorage.getItem(DASHBOARD_ACCESS_KEY) === '1');
  const [preset, setPreset] = useState<TimeRangePreset>('last_24_hours');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [compareOn, setCompareOn] = useState(false);
  const [mapLayer, setMapLayer] = useState<'sentiment' | 'density'>('sentiment');
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [trends, setTrends] = useState<DashboardTrends | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [favoriteNeighborhoodId, setFavoriteNeighborhoodId] = useState<string | undefined>(
    localStorage.getItem(FAVORITE_KEY) || undefined
  );
  const [favoriteOptions, setFavoriteOptions] = useState<DashboardAreaOption[]>([]);
  const [favoriteSearch, setFavoriteSearch] = useState('');
  const [favoriteMenuOpen, setFavoriteMenuOpen] = useState(false);
  const [trendLevel, setTrendLevel] = useState<'city' | 'borough' | 'neighborhood' | 'block_group'>('city');
  const [trendAreaId, setTrendAreaId] = useState<string | undefined>(undefined);
  const [trendAreaOptions, setTrendAreaOptions] = useState<DashboardAreaOption[]>([]);
  const [trendMetric, setTrendMetric] = useState<'sentiment' | 'votes'>('sentiment');
  const [selectedMapArea, setSelectedMapArea] = useState<MapFeatureProperties | null>(null);
  const [alertMessages, setAlertMessages] = useState<string[]>([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'qotd'>('overview');
  const [demoVoteStatus, setDemoVoteStatus] = useState<DemoVoteAutomationStatus | null>(null);
  const [demoVoteEnabled, setDemoVoteEnabled] = useState(false);
  const [demoVoteLoading, setDemoVoteLoading] = useState(false);
  const [demoVoteMessage, setDemoVoteMessage] = useState<string | null>(null);
  const customStartInputRef = useRef<HTMLInputElement | null>(null);
  const customEndInputRef = useRef<HTMLInputElement | null>(null);

  const range = useMemo(() => buildTimeRange(preset, customStart, customEnd), [preset, customStart, customEnd]);
  const [liveRange, setLiveRange] = useState(range);
  const favoriteSelectValue = useMemo(
    () => (favoriteNeighborhoodId && favoriteOptions.some((option) => option.id === favoriteNeighborhoodId) ? favoriteNeighborhoodId : ''),
    [favoriteNeighborhoodId, favoriteOptions]
  );
  const trendAreaSelectValue = useMemo(
    () => (trendAreaId && trendAreaOptions.some((option) => option.id === trendAreaId) ? trendAreaId : ''),
    [trendAreaId, trendAreaOptions]
  );
  const filteredFavoriteOptions = useMemo(() => {
    const query = favoriteSearch.trim().toLowerCase();
    if (!query) return favoriteOptions;
    return favoriteOptions.filter((option) => option.name.toLowerCase().startsWith(query));
  }, [favoriteOptions, favoriteSearch]);
  const cityCard = useMemo(
    () => overview?.cards.find((card) => card.area_type === 'city') || null,
    [overview?.cards]
  );
  const favoriteCard = useMemo(
    () => overview?.cards.find((card) => card.is_favorite) || null,
    [overview?.cards]
  );
  const secondaryCards = useMemo(
    () =>
      (overview?.cards || []).filter((card) => {
        if (cityCard && card.id === cityCard.id) return false;
        if (favoriteCard && card.id === favoriteCard.id) return false;
        return true;
      }),
    [overview?.cards, cityCard, favoriteCard]
  );
  const cityLogo = useMemo(() => {
    if (!cityCard) return '/favicon.ico';
    const cityPositive = getPositivePercentage(cityCard);
    return getCityLogoByPercentage(cityPositive) || '/favicon.ico';
  }, [cityCard]);
  const favoriteEmotionIcon = useMemo(() => {
    if (!favoriteCard) return null;
    return getEmotionIconByPercentage(getPositivePercentage(favoriteCard));
  }, [favoriteCard]);
  const votesPerActiveArea = useMemo(() => {
    const totalVotes = overview?.engagement.total_votes || 0;
    const activeAreas = overview?.engagement.active_areas || 0;
    if (!activeAreas) return 0;
    return totalVotes / activeAreas;
  }, [overview?.engagement.total_votes, overview?.engagement.active_areas]);

  const handleUnlock = () => {
    const expectedPassword = process.env.REACT_APP_DASHBOARD_PASSWORD || 'hwa-nyc-internal';
    if (password === expectedPassword) {
      localStorage.setItem(DASHBOARD_ACCESS_KEY, '1');
      setAccessGranted(true);
      setError(null);
      return;
    }
    setError('Incorrect password.');
  };

  const handleLogout = () => {
    localStorage.removeItem(DASHBOARD_ACCESS_KEY);
    setAccessGranted(false);
    setPassword('');
  };

  const resolveFavoriteFromGeolocation = useCallback(async () => {
    if (favoriteNeighborhoodId) return;
    try {
      const location = await votingService.getCurrentLocation();
      const locationCheck = await votingService.checkLocation(location.latitude, location.longitude);
      if (locationCheck.area_hierarchy?.neighborhood_id) {
        setFavoriteNeighborhoodId(locationCheck.area_hierarchy.neighborhood_id);
        localStorage.setItem(FAVORITE_KEY, locationCheck.area_hierarchy.neighborhood_id);
      }
    } catch {
      // Best effort only.
    }
  }, [favoriteNeighborhoodId]);

  const loadOverview = useCallback(async (options?: { silent?: boolean; rangeOverride?: { startAt: Date; endAt: Date } }) => {
    const activeRange = options?.rangeOverride ?? range;
    if (!options?.silent) {
      setOverviewLoading(true);
    }
    try {
      const data = await dashboardApi.getOverview({
        startAt: activeRange.startAt,
        endAt: activeRange.endAt,
        favoriteNeighborhoodId,
      });
      setOverview(data);
      if (data.favorite_neighborhood_id) {
        setFavoriteNeighborhoodId(data.favorite_neighborhood_id);
        localStorage.setItem(FAVORITE_KEY, data.favorite_neighborhood_id);
      }
      if (!trendAreaId && data.cards.length > 0) {
        const cityCard = data.cards.find((card) => card.area_type === 'city');
        if (cityCard) {
          setTrendAreaId(cityCard.id);
          setTrendLevel('city');
        }
      }
    } catch (err) {
      setError('Unable to load dashboard overview.');
      console.error(err);
    } finally {
      if (!options?.silent) {
        setOverviewLoading(false);
      }
    }
  }, [range, favoriteNeighborhoodId, trendAreaId]);

  const loadTrends = useCallback(
    async (options?: { silent?: boolean; rangeOverride?: { startAt: Date; endAt: Date } }) => {
      const activeRange = options?.rangeOverride ?? range;
    if (!trendAreaId && trendLevel !== 'city') return;
    if (!options?.silent) {
      setTrendsLoading(true);
    }
    try {
      const data = await dashboardApi.getTrends({
        startAt: activeRange.startAt,
        endAt: activeRange.endAt,
        level: trendLevel,
        areaId: trendAreaId,
        includePrevious: compareOn,
      });
      setTrends(data);
    } catch (err) {
      setError('Unable to load trends.');
      console.error(err);
    } finally {
      if (!options?.silent) {
        setTrendsLoading(false);
      }
    }
    },
    [range, trendAreaId, trendLevel, compareOn]
  );

  const resolveDemoVoteError = (err: unknown, fallback: string) => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return 'Demo vote API is not deployed yet. Redeploy the backend to enable this control.';
    }
    return fallback;
  };

  const loadDemoVoteStatus = useCallback(async () => {
    setDemoVoteLoading(true);
    try {
      const status = await dashboardApi.getDemoVoteStatus();
      setDemoVoteStatus(status);
      setDemoVoteEnabled(status.enabled);
      setDemoVoteMessage(null);
    } catch (err) {
      console.error(err);
      setDemoVoteMessage(resolveDemoVoteError(err, 'Unable to load demo vote automation.'));
    } finally {
      setDemoVoteLoading(false);
    }
  }, []);

  const handleDemoVoteToggle = async (enabled: boolean) => {
    const previousEnabled = demoVoteEnabled;
    setDemoVoteEnabled(enabled);
    setDemoVoteLoading(true);
    setDemoVoteMessage(null);
    try {
      const status = await dashboardApi.setDemoVoteEnabled(enabled);
      setDemoVoteStatus(status);
      setDemoVoteEnabled(status.enabled);
      setDemoVoteMessage(enabled ? 'Demo votes enabled.' : 'Demo votes disabled.');
    } catch (err) {
      console.error(err);
      setDemoVoteEnabled(previousEnabled);
      setDemoVoteMessage(resolveDemoVoteError(err, 'Unable to update demo vote automation.'));
    } finally {
      setDemoVoteLoading(false);
    }
  };

  const handleRunDemoVotesNow = async () => {
    setDemoVoteLoading(true);
    setDemoVoteMessage(null);
    try {
      const status = await dashboardApi.runDemoVotesNow();
      setDemoVoteStatus(status);
      setDemoVoteMessage(`Inserted ${status.inserted_count ?? status.last_inserted_count} demo votes.`);
      loadOverview({ silent: true });
      loadTrends({ silent: true });
    } catch (err) {
      console.error(err);
      setDemoVoteMessage(resolveDemoVoteError(err, 'Unable to run demo votes.'));
    } finally {
      setDemoVoteLoading(false);
    }
  };

  useEffect(() => {
    if (!accessGranted) return;
    resolveFavoriteFromGeolocation();
  }, [accessGranted, resolveFavoriteFromGeolocation]);

  useEffect(() => {
    if (!accessGranted) return;
    loadDemoVoteStatus();
  }, [accessGranted, loadDemoVoteStatus]);

  useEffect(() => {
    if (!accessGranted) return;
    loadOverview();
  }, [accessGranted, loadOverview]);

  useEffect(() => {
    if (!accessGranted) return;
    loadTrends();
  }, [accessGranted, loadTrends]);

  useEffect(() => {
    setLiveRange(range);
  }, [range]);

  useEffect(() => {
    if (!accessGranted) return;
    const interval = window.setInterval(() => {
      if (document.hidden) return;
      if (!isAutoRefreshPreset(preset)) {
        loadOverview({ silent: true });
        loadTrends({ silent: true });
        return;
      }
      const refreshedRange = buildTimeRange(preset, customStart, customEnd);
      setLiveRange(refreshedRange);
      loadOverview({ silent: true, rangeOverride: refreshedRange });
      loadTrends({ silent: true, rangeOverride: refreshedRange });
    }, DASHBOARD_AUTO_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [accessGranted, loadOverview, loadTrends, preset, customStart, customEnd]);

  useEffect(() => {
    if (!accessGranted) return;
    const timeout = window.setTimeout(async () => {
      try {
        const options = await dashboardApi.getAreas('neighborhood');
        setFavoriteOptions(dedupeAreaOptions(options));
      } catch (err) {
        console.error(err);
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [accessGranted]);

  useEffect(() => {
    if (!accessGranted) return;
    const timeout = window.setTimeout(async () => {
      try {
        const options = await dashboardApi.getAreas(trendLevel);
        const deduped = dedupeAreaOptions(options);
        setTrendAreaOptions(deduped);
        if (!trendAreaId && deduped[0]) {
          setTrendAreaId(deduped[0].id);
        }
      } catch (err) {
        console.error(err);
      }
    }, 150);
    return () => window.clearTimeout(timeout);
  }, [trendLevel, accessGranted, trendAreaId]);

  useEffect(() => {
    if (!trendAreaId) return;
    const stillExists = trendAreaOptions.some((option) => option.id === trendAreaId);
    if (!stillExists) {
      setTrendAreaId(trendAreaOptions[0]?.id);
    }
  }, [trendAreaId, trendAreaOptions]);

  useEffect(() => {
    if (!selectedMapArea) {
      setAlertMessages(['No alerts in the selected range.']);
      return;
    }
    const nextAlerts: string[] = [];
    if (selectedMapArea.sentiment_index <= ALERT_SENTIMENT_DROP_THRESHOLD) {
      nextAlerts.push('Sudden sentiment drop detected (SI below -10).');
    }
    if (selectedMapArea.total_votes >= ALERT_VOTE_SPIKE_THRESHOLD) {
      nextAlerts.push('Vote spike detected versus baseline threshold.');
    }
    if (nextAlerts.length === 0) {
      nextAlerts.push('No alerts in the selected range.');
    }
    setAlertMessages(nextAlerts);
  }, [selectedMapArea]);

  const handleMapSelection = (feature: MapFeatureProperties | null) => {
    setSelectedMapArea(feature);
    if (feature) {
      const mappedLevel = feature.area_type as 'city' | 'borough' | 'neighborhood' | 'block_group';
      setTrendLevel(mappedLevel);
      setTrendAreaId(feature.id);
    }
  };

  const exportTrendsCsv = () => {
    if (!trends) return;
    const header = 'series,bucket,sentiment_index,votes';
    const currentRows = trends.current.map((point) => `current,${point.bucket},${point.sentiment_index},${point.votes}`);
    const previousRows = compareOn
      ? trends.previous.map((point) => `previous,${point.bucket},${point.sentiment_index},${point.votes}`)
      : [];
    const csv = [header, ...currentRows, ...previousRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `hwa-trends-${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const exportTrendsPdf = () => {
    if (!trends) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const currentRows = trends.current
      .slice(-20)
      .map(
        (point) =>
          `<tr><td>${point.bucket}</td><td>${Math.round(point.sentiment_index)}</td><td>${formatVotes(point.votes)}</td></tr>`
      )
      .join('');
    printWindow.document.write(`
      <html>
        <head><title>HWA Trends Export</title></head>
        <body>
          <h1>HWA NYC Control Panel - Trends Export</h1>
          <p>Range: ${formatRangeLabel(range.startAt.toISOString(), range.endAt.toISOString())}</p>
          <p>Level: ${trendLevel} | Area: ${trendAreaId || 'city'}</p>
          <table border="1" cellspacing="0" cellpadding="6">
            <thead><tr><th>Bucket</th><th>Sentiment Index</th><th>Votes</th></tr></thead>
            <tbody>${currentRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const openDatePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    const pickerCapable = input as HTMLInputElement & { showPicker?: () => void };
    if (pickerCapable.showPicker) {
      pickerCapable.showPicker();
      return;
    }
    input.focus();
  };

  const renderMapSidePanel = () => (
    <Stack spacing={2}>
      <Paper className="dashboard-section-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Layer Controls
        </Typography>
        <ToggleButtonGroup
          className="dashboard-layer-toggle"
          value={mapLayer}
          exclusive
          onChange={(_, value) => value && setMapLayer(value)}
          size="small"
          fullWidth
        >
          <ToggleButton value="sentiment">Sentiment</ToggleButton>
          <ToggleButton value="density">Vote Density</ToggleButton>
        </ToggleButtonGroup>
      </Paper>
      <Paper className="dashboard-section-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Selected Area Details
        </Typography>
        {selectedMapArea ? (
          <Stack spacing={0.6}>
            <Typography variant="subtitle1">{selectedMapArea.name}</Typography>
            {selectedMapArea.area_type === 'block_group' && selectedMapArea.neighborhood_name ? (
              <Typography className="dashboard-area-neighborhood-note">({selectedMapArea.neighborhood_name})</Typography>
            ) : null}
            <Typography variant="body2">Type: {selectedMapArea.area_type}</Typography>
            <Typography className="dashboard-selected-sentiment">
              Sentiment Index: {selectedMapArea.sentiment_index.toFixed(1)}
            </Typography>
            <Typography className="dashboard-selected-votes-main">{formatVotes(selectedMapArea.total_votes)} votes</Typography>
            <Typography className="dashboard-selected-votes-ratio">
              <span className="dashboard-ratio-strong">
                {selectedMapArea.total_votes > 0 ? ((selectedMapArea.good_votes / selectedMapArea.total_votes) * 100).toFixed(1) : '0.0'}%
              </span>{' '}
              good ·{' '}
              <span className="dashboard-ratio-strong">
                {selectedMapArea.total_votes > 0 ? ((selectedMapArea.bad_votes / selectedMapArea.total_votes) * 100).toFixed(1) : '0.0'}%
              </span>{' '}
              bad
            </Typography>
            {typeof selectedMapArea.density_percent === 'number' && (
              <Typography variant="body2">
                Vote density: <span className="dashboard-ratio-strong">{selectedMapArea.density_percent.toFixed(1)}%</span>
              </Typography>
            )}
            <Chip
              className="dashboard-confidence-chip"
              size="small"
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              icon={
                <span className="material-symbols-outlined dashboard-chip-icon">
                  {selectedMapArea.confidence >= 0.95 ? 'thumbs_up_double' : 'error'}
                </span>
              }
              label={formatConfidenceBadge(selectedMapArea.confidence).toLowerCase()}
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click any area on the map to inspect details.
          </Typography>
        )}
      </Paper>
      <Paper className="dashboard-section-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Basic Alerts
        </Typography>
        <List dense sx={{ p: 0 }}>
          {alertMessages.map((message) => (
            <ListItem key={message} sx={{ px: 0 }}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={message} />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Stack>
  );

  const renderCardMetrics = (card: DashboardCard, options?: { showHeader?: boolean }) => {
    const showHeader = options?.showHeader ?? true;
    const positivePercentage = getPositivePercentage(card);
    const sentimentColor = card.total_votes ? getColorForPercentage(positivePercentage) : NO_VOTES_COLOR;
    const emotionIcon = card.total_votes > 0 ? getEmotionIconByPercentage(positivePercentage) : getNoVotesEmotionIcon();
    const confidenceIcon = card.confidence >= 0.95 ? 'thumbs_up_double' : 'error';
    const confidenceLabel = formatConfidenceBadge(card.confidence).toLowerCase();
    const deltaValue = `${card.delta_vs_previous >= 0 ? '+' : ''}${card.delta_vs_previous.toFixed(1)}`;

    return (
      <>
        {showHeader ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" className="dashboard-card-title-row">
            <Typography className="dashboard-card-title">{card.name}</Typography>
            {emotionIcon ? <img src={emotionIcon} className="dashboard-card-emotion" alt={`${card.name} emotion`} /> : null}
          </Stack>
        ) : null}
        <Typography className="dashboard-card-score" sx={{ color: sentimentColor }}>
          {formatSentiment(card.sentiment_index)}
        </Typography>
        <Box className="dashboard-card-delta">
          <span className="material-symbols-outlined dashboard-inline-icon">
            {card.delta_vs_previous >= 0 ? 'arrow_drop_up' : 'arrow_drop_down'}
          </span>
          <span className="dashboard-delta-value">{deltaValue}</span>
          <span className="dashboard-delta-caption">vs previous period</span>
        </Box>
        <Chip
          className="dashboard-confidence-chip"
          icon={<span className="material-symbols-outlined dashboard-chip-icon">{confidenceIcon}</span>}
          label={confidenceLabel}
          size="small"
        />
        <Typography className="dashboard-card-votes">{formatVotes(card.total_votes)} votes</Typography>
        <Typography className="dashboard-card-ratio">
          <span className="dashboard-ratio-strong">{positivePercentage.toFixed(1)}%</span> good ·{' '}
          <span className="dashboard-ratio-strong">{(100 - positivePercentage).toFixed(1)}%</span> bad
        </Typography>
      </>
    );
  };

  if (!accessGranted) {
    return (
      <Box className="dashboard-access-root">
        <Card className="dashboard-access-card">
          <CardContent className="dashboard-access-content">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.8 }}>
              <img src={dashboardAccessLogo} alt="HWA NYC logo" className="dashboard-access-logo" />
              <Typography className="dashboard-access-title">
                <span className="dashboard-access-title-strong">HWA NYC</span>
                <span className="dashboard-access-title-light">Dashboard Access</span>
              </Typography>
            </Stack>
            <Typography className="dashboard-access-subtitle">
              Internal access only. Enter dashboard password.
            </Typography>
            <Stack spacing={1.6}>
              <TextField
                type="password"
                label="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleUnlock();
                }}
                className="dashboard-access-input"
                fullWidth
              />
              <Button variant="contained" onClick={handleUnlock} className="dashboard-access-button">
                Unlock Dashboard
              </Button>
              {error && (
                <Alert severity="error" className="dashboard-access-alert">
                  {error}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="dashboard-root">
      <Paper className="dashboard-sticky-header" elevation={0}>
        <Box className="dashboard-shell dashboard-header-shell">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
            <Stack direction="row" spacing={4} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
              <img src={cityLogo} className="dashboard-header-logo" alt="HWA NYC sentiment logo" />
              <Box className="dashboard-header-main">
                <Typography className="dashboard-header-title">
                  <span className="dashboard-header-title-strong">HWA NYC ·</span>{' '}
                  <span className="dashboard-header-title-light">Sentiment Analysis Dashboard</span>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" className="dashboard-header-controls-row">
                  <FormControl size="small" className="dashboard-time-range-control">
                    <Select
                      value={preset}
                      onChange={(event) => setPreset(event.target.value as TimeRangePreset)}
                      IconComponent={() => <span className="material-symbols-outlined">arrow_drop_down</span>}
                      renderValue={(value) => {
                        const selected = timeRangeOptions.find((option) => option.value === value);
                        return (
                          <span className="dashboard-select-inline-wrap">
                            <span className="dashboard-select-label">Time Range</span>
                            <span className="dashboard-select-value">{selected?.label || value}</span>
                          </span>
                        );
                      }}
                      MenuProps={{
                        PaperProps: { className: 'dashboard-menu-paper' },
                        MenuListProps: { className: 'dashboard-menu-list' },
                      }}
                    >
                      {timeRangeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value} className="dashboard-menu-item">
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {preset === 'custom' && (
                    <Stack direction="row" spacing={1} className="dashboard-custom-range-row">
                      <TextField
                        size="small"
                        type="date"
                        value={customStart}
                        onChange={(event) => setCustomStart(event.target.value)}
                        className="dashboard-date-input"
                        inputRef={customStartInputRef}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => openDatePicker(customStartInputRef.current)}
                                edge="end"
                              >
                                <span className="material-symbols-outlined dashboard-date-icon">calendar_month</span>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        value={customEnd}
                        onChange={(event) => setCustomEnd(event.target.value)}
                        className="dashboard-date-input"
                        inputRef={customEndInputRef}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => openDatePicker(customEndInputRef.current)}
                                edge="end"
                              >
                                <span className="material-symbols-outlined dashboard-date-icon">calendar_month</span>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  )}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    className="dashboard-demo-votes-control"
                    sx={{
                      border: '1px solid rgba(255,255,255,0.16)',
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.75,
                      flexWrap: 'wrap',
                    }}
                  >
                    <FormControlLabel
                      className="dashboard-demo-votes-label"
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={demoVoteEnabled}
                          disabled={demoVoteLoading}
                          onChange={(event) => handleDemoVoteToggle(event.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="caption" className="dashboard-demo-votes-title">
                            Demo votes
                          </Typography>
                          <Typography variant="caption" className="dashboard-demo-votes-caption">
                            Last: {formatDemoRun(demoVoteStatus?.last_run_at ?? null)}
                          </Typography>
                        </Box>
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      className="dashboard-demo-votes-button"
                      disabled={demoVoteLoading}
                      onClick={handleRunDemoVotesNow}
                    >
                      {demoVoteLoading ? 'Working...' : 'Run now'}
                    </Button>
                  </Stack>
                </Stack>
                {demoVoteMessage ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" className="dashboard-demo-votes-message">
                      {demoVoteMessage}
                    </Typography>
                    {!demoVoteStatus ? (
                      <Button
                        size="small"
                        variant="text"
                        className="dashboard-demo-votes-retry"
                        disabled={demoVoteLoading}
                        onClick={loadDemoVoteStatus}
                      >
                        Retry
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Box>
            </Stack>
            <Button
              onClick={handleLogout}
              className="dashboard-logout-button"
              startIcon={<span className="material-symbols-outlined">logout</span>}
            >
              Logout
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Box className="dashboard-shell dashboard-content-shell">
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs
          className="dashboard-main-tabs"
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ mb: 2 }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="qotd" label="Question of the Day" />
        </Tabs>

        {activeTab === 'overview' ? (
        <>
        <Box className="dashboard-cards-layout">
          <Box className="dashboard-top-cards">
            {cityCard ? (
              <Card className="dashboard-kpi-card dashboard-kpi-card--hero">
                <CardContent className="dashboard-card-content">{renderCardMetrics(cityCard)}</CardContent>
              </Card>
            ) : null}
            {favoriteCard ? (
              <Card className="dashboard-kpi-card dashboard-kpi-card--hero">
                <CardContent className="dashboard-card-content dashboard-favorite-card-content">
                  <Box className="dashboard-favorite-top-row">
                    <Typography className="dashboard-favorite-title">{favoriteCard.name}</Typography>
                    {favoriteEmotionIcon ? (
                      <img
                        src={favoriteEmotionIcon}
                        className="dashboard-card-emotion dashboard-card-emotion--favorite"
                        alt={`${favoriteCard.name} emotion`}
                      />
                    ) : null}
                  </Box>
                  <Box className="dashboard-favorite-controls">
                    <Stack spacing={1}>
                      <FormControl size="small">
                        <Select
                          value={favoriteSelectValue}
                          open={favoriteMenuOpen}
                          onOpen={() => setFavoriteMenuOpen(true)}
                          onClose={() => {
                            setFavoriteMenuOpen(false);
                            setFavoriteSearch('');
                          }}
                          onChange={(event) => {
                            const value = event.target.value;
                            setFavoriteNeighborhoodId(value || undefined);
                            if (value) localStorage.setItem(FAVORITE_KEY, value);
                          }}
                          IconComponent={() => <span className="material-symbols-outlined">arrow_drop_down</span>}
                          MenuProps={{
                            PaperProps: { className: 'dashboard-menu-paper' },
                            MenuListProps: { className: 'dashboard-menu-list', autoFocusItem: false },
                          }}
                        >
                          <ListSubheader className="dashboard-menu-search-header">
                            <TextField
                              size="small"
                              autoFocus
                              placeholder="Search neighborhood"
                              value={favoriteSearch}
                              onChange={(event) => setFavoriteSearch(event.target.value)}
                              className="dashboard-menu-search-input"
                              onKeyDown={(event) => {
                                if (event.key !== 'Escape') {
                                  event.stopPropagation();
                                }
                              }}
                            />
                          </ListSubheader>
                          {filteredFavoriteOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id} className="dashboard-menu-item">
                              {option.name}
                            </MenuItem>
                          ))}
                          {filteredFavoriteOptions.length === 0 && (
                            <MenuItem disabled className="dashboard-menu-item dashboard-menu-item--empty">
                              No matches
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                  <Box className="dashboard-favorite-metrics">{renderCardMetrics(favoriteCard, { showHeader: false })}</Box>
                </CardContent>
              </Card>
            ) : null}
          </Box>

          <Box className="dashboard-secondary-cards">
            {secondaryCards.map((card) => (
              <Card key={card.id} className="dashboard-kpi-card">
                <CardContent className="dashboard-card-content">{renderCardMetrics(card)}</CardContent>
              </Card>
            ))}
          </Box>

          {overviewLoading && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Loading KPI cards...
              </Typography>
            </Box>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={9}>
            <DashboardMap
              startAt={liveRange.startAt.toISOString()}
              endAt={liveRange.endAt.toISOString()}
              layer={mapLayer}
              onAreaSelected={handleMapSelection}
              actionSlot={
                <Button
                  variant="outlined"
                  className="dashboard-map-expand-button"
                  startIcon={<span className="material-symbols-outlined">open_in_full</span>}
                  onClick={() => setMapModalOpen(true)}
                >
                  Expand
                </Button>
              }
            />
          </Grid>
          <Grid item xs={12} lg={3}>{renderMapSidePanel()}</Grid>
        </Grid>

        <Dialog
          open={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
          fullScreen
          PaperProps={{ className: 'dashboard-map-modal-paper' }}
        >
          <Box className="dashboard-map-modal-close-row">
            <IconButton className="dashboard-map-modal-close-button" onClick={() => setMapModalOpen(false)} aria-label="Close map modal">
              <span className="material-symbols-outlined">close</span>
            </IconButton>
          </Box>
          <DialogContent className="dashboard-map-modal-content">
            <Box className="dashboard-map-modal-layout">
              <Box className="dashboard-map-modal-map-col">
                <DashboardMap
                  startAt={liveRange.startAt.toISOString()}
                  endAt={liveRange.endAt.toISOString()}
                  layer={mapLayer}
                  onAreaSelected={handleMapSelection}
                  fillHeight
                />
              </Box>
              <Box className="dashboard-map-modal-side-col">{renderMapSidePanel()}</Box>
            </Box>
          </DialogContent>
        </Dialog>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Paper className="dashboard-section-card" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Trends
                </Typography>
                <FormControl size="small" sx={{ minWidth: 160 }} className="dashboard-trends-select-control">
                  <Select
                    value={trendLevel}
                    IconComponent={() => <span className="material-symbols-outlined">arrow_drop_down</span>}
                    renderValue={(value) => {
                      const selected = levelOptions.find((option) => option.value === value);
                      return (
                        <span className="dashboard-select-inline-wrap">
                          <span className="dashboard-select-label">Level</span>
                          <span className="dashboard-select-value">{selected?.label || value}</span>
                        </span>
                      );
                    }}
                    MenuProps={{
                      PaperProps: { className: 'dashboard-menu-paper' },
                      MenuListProps: { className: 'dashboard-menu-list' },
                    }}
                    onChange={(event) =>
                      setTrendLevel(event.target.value as 'city' | 'borough' | 'neighborhood' | 'block_group')
                    }
                  >
                    {levelOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} className="dashboard-menu-item">
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }} className="dashboard-trends-select-control">
                  <Select
                    value={trendAreaSelectValue}
                    displayEmpty
                    IconComponent={() => <span className="material-symbols-outlined">arrow_drop_down</span>}
                    renderValue={(value) => {
                      const selected = trendAreaOptions.find((option) => option.id === value);
                      return (
                        <span className="dashboard-select-inline-wrap">
                          <span className="dashboard-select-label">Area</span>
                          <span className="dashboard-select-value">{selected?.name || 'Select'}</span>
                        </span>
                      );
                    }}
                    MenuProps={{
                      PaperProps: { className: 'dashboard-menu-paper' },
                      MenuListProps: { className: 'dashboard-menu-list' },
                    }}
                    onChange={(event) => setTrendAreaId(event.target.value || undefined)}
                  >
                    {trendAreaOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id} className="dashboard-menu-item">
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }} className="dashboard-trends-select-control">
                  <Select
                    value={trendMetric}
                    IconComponent={() => <span className="material-symbols-outlined">arrow_drop_down</span>}
                    renderValue={(value) => {
                      const selected = trendMetricOptions.find((option) => option.value === value);
                      return (
                        <span className="dashboard-select-inline-wrap">
                          <span className="dashboard-select-label">Metric</span>
                          <span className="dashboard-select-value">{selected?.label || value}</span>
                        </span>
                      );
                    }}
                    MenuProps={{
                      PaperProps: { className: 'dashboard-menu-paper' },
                      MenuListProps: { className: 'dashboard-menu-list' },
                    }}
                    onChange={(event) => setTrendMetric(event.target.value as 'sentiment' | 'votes')}
                  >
                    {trendMetricOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value} className="dashboard-menu-item">
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {trendsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading trends...
                </Typography>
              ) : (
                <TrendChart
                  trends={trends}
                  compareOn={compareOn}
                  metric={trendMetric}
                  preset={preset}
                  startAt={liveRange.startAt.toISOString()}
                  endAt={liveRange.endAt.toISOString()}
                />
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mt: 1.5 }} alignItems={{ md: 'center' }}>
                <FormControlLabel
                  sx={{ ml: { md: 0.25 } }}
                  control={<Switch checked={compareOn} onChange={(event) => setCompareOn(event.target.checked)} />}
                  label="Compare previous"
                />
                <Button
                  variant="outlined"
                  onClick={exportTrendsCsv}
                  startIcon={<span className="material-symbols-outlined">table</span>}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  onClick={exportTrendsPdf}
                  startIcon={<span className="material-symbols-outlined">docs</span>}
                >
                  Export PDF
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Paper className="dashboard-section-card" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Rankings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '1rem' }}>
                    Top Positive Neighborhoods
                  </Typography>
                  <List dense>
                    {overview?.rankings.top_positive.map((item) => (
                      <ListItem key={item.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={`SI ${Math.round(item.sentiment_index)} • ${formatVotes(item.total_votes)} votes`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '1rem' }}>
                    Top Negative Neighborhoods
                  </Typography>
                  <List dense>
                    {overview?.rankings.top_negative.map((item) => (
                      <ListItem key={item.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={`SI ${Math.round(item.sentiment_index)} • ${formatVotes(item.total_votes)} votes`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '1rem' }}>
                    Movers
                  </Typography>
                  <List dense>
                    {[...(overview?.rankings.movers_up || []), ...(overview?.rankings.movers_down || [])].map((item, index) => (
                      <ListItem key={`${item.id}-${item.delta >= 0 ? 'up' : 'down'}-${index}`} sx={{ px: 0 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={
                            <Box className="dashboard-mover-secondary">
                              <span
                                className={`material-symbols-outlined dashboard-mover-icon ${
                                  item.delta >= 0 ? 'dashboard-mover-icon--up' : 'dashboard-mover-icon--down'
                                }`}
                              >
                                {item.delta >= 0 ? 'north' : 'south'}
                              </span>
                              <span className={item.delta >= 0 ? 'dashboard-mover-delta--up' : 'dashboard-mover-delta--down'}>
                                {formatMoverDelta(item.delta)}
                              </span>
                              <span>• {formatVotes(item.total_votes)} votes</span>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Paper className="dashboard-section-card" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Engagement
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" className="dashboard-engagement-card">
                    <CardContent>
                      <Typography className="dashboard-engagement-label">
                        Votes per hour
                      </Typography>
                      <Typography className="dashboard-engagement-value">
                        {(overview?.engagement.votes_per_hour || 0).toFixed(1)}
                      </Typography>
                      <MiniSparkline points={overview?.engagement.sparkline || []} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" className="dashboard-engagement-card">
                    <CardContent>
                      <Typography className="dashboard-engagement-label">
                        Active areas
                      </Typography>
                      <Typography className="dashboard-engagement-value">
                        {formatVotes(overview?.engagement.active_areas || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" className="dashboard-engagement-card">
                    <CardContent>
                      <Typography className="dashboard-engagement-label">
                        Votes per active area
                      </Typography>
                      <Typography className="dashboard-engagement-value">
                        {votesPerActiveArea.toFixed(1)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" className="dashboard-engagement-card">
                    <CardContent>
                      <Typography className="dashboard-engagement-label">
                        Total votes
                      </Typography>
                      <Typography className="dashboard-engagement-value">
                        {formatVotes(overview?.engagement.total_votes || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className="dashboard-section-card" sx={{ p: 2 }}>
              <Box className="dashboard-insights-header">
                <Typography variant="h6">Insights (Coming Soon)</Typography>
                <TextField
                  size="small"
                  placeholder="Ask Me Anything"
                  fullWidth
                  className="dashboard-insights-prompt-input"
                  InputProps={{ readOnly: true }}
                  onClick={(event) => event.preventDefault()}
                />
              </Box>
              <Grid container spacing={2} className="dashboard-insights-grid">
                {insightSamples.map((insight) => (
                  <Grid item xs={12} md={4} key={insight.id}>
                    <Card variant="outlined" className="dashboard-insight-card">
                      <CardContent>
                        <Typography className="dashboard-insight-title">{insight.title}</Typography>
                        <Typography className="dashboard-insight-body">{insight.excerpt}</Typography>
                        {insight.tag ? (
                          <Box className="dashboard-insight-tag">
                            {insight.tagIcon ? (
                              <span className="material-symbols-outlined dashboard-insight-tag-icon">{insight.tagIcon}</span>
                            ) : null}
                            <span>{insight.tag}</span>
                          </Box>
                        ) : null}
                        <Button
                          variant="text"
                          className="dashboard-insight-link"
                          href="#"
                          onClick={(event) => event.preventDefault()}
                        >
                          Read full insight →
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" className="dashboard-insights-footnote" sx={{ mt: 1.5, display: 'block' }}>
                Insights will be available to approved partner accounts.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box className="dashboard-bottom-footer">
          <p className="dashboard-bottom-footer-text">
            Made in NYC · <a href="/about.html" className="dashboard-bottom-footer-link">About</a> ·{' '}
            <a href="https://shop.hwa.nyc/" className="dashboard-bottom-footer-link" target="_blank" rel="noopener noreferrer">Merch</a> ·{' '}
            <a href="/privacy.html" className="dashboard-bottom-footer-link">Privacy</a> ·{' '}
            <a href="/terms.html" className="dashboard-bottom-footer-link">Terms</a> ·{' '}
            <a href="/support.html" className="dashboard-bottom-footer-link">Support</a> ·{' '}
            <span className="dashboard-bottom-footer-socials" aria-label="Social links">
              <a href="https://www.instagram.com/hwa.nyc/" className="dashboard-bottom-footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <img src={instagramIcon} alt="" />
              </a>
              <a href="https://www.tiktok.com/@hwa...nyc" className="dashboard-bottom-footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <img src={tiktokIcon} alt="" />
              </a>
              <a href="https://bsky.app/profile/hwa.nyc" className="dashboard-bottom-footer-social-link" target="_blank" rel="noopener noreferrer" aria-label="Bluesky">
                <img src={blueskyIcon} alt="" />
              </a>
            </span>{' '}
            <a href="https://hwa.today" className="dashboard-bottom-footer-link" target="_blank" rel="noopener noreferrer">
              hwa.today
            </a>
          </p>
        </Box>
        </>
        ) : (
          <DashboardQotdTab />
        )}
      </Box>
    </Box>
  );
};

export default DashboardPage;
